import { api } from '../../../../../lib/api';
import { ALLIANCE_COMMENT_PAGE_SIZE } from './allianceBoardApi';

function safeJsonParse(value, fallback) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function fileNameOf(file) {
  return file?.orgFileNm || file?.fileNm || file?.name || '첨부파일';
}

function fileSizeText(file) {
  const size = Number(file?.fileSize || 0);
  if (!size) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)}MB`;
  if (size >= 1024) return `${Math.round(size / 1024)}KB`;
  return `${size}B`;
}

function fileViewUrl(file) {
  if (file?.fileUid) return `/api/glow/files/viewByUid/${file.fileUid}`;
  if (file?.fileId) return `/api/glow/files/view?fileId=${encodeURIComponent(file.fileId)}`;
  return '';
}

function mapFile(file) {
  return {
    id: file.fileId || file.fileUid,
    name: fileNameOf(file),
    size: fileSizeText(file),
    url: fileViewUrl(file),
    fileId: file.fileId,
    fileUid: file.fileUid,
    raw: file,
  };
}

function mapStatus(statusCd) {
  return statusCd || 'PLANNED';
}

function tagsFromFormData(formData) {
  const parsed = safeJsonParse(formData, {});
  return Array.isArray(parsed?.tags) ? parsed.tags : [];
}

/** 목록 카드용 본문 미리보기: HTML 제거 */
function stripHtml(html) {
  if (html == null || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
}

function listItemSummaryPreview(item) {
  const d = item.description != null ? String(item.description).trim() : '';
  if (d) return d;
  return stripHtml(item.content || '');
}

function mapProjectCard(item) {
  return {
    id: item.milestoneUid,
    title: item.title || '',
    org: item.ownerNm || '',
    person: item.ownerNm || '',
    userId: item.ownerId || '',
    progress: Number(item.progressRate || 0),
    statusTag: mapStatus(item.statusCd),
    thumb: item.thumbnailFileUid ? `/api/glow/files/viewByUid/${item.thumbnailFileUid}` : '',
    updatedAt: item.updtDt || item.regDt || '',
    progressUpdatedAt: item.updtDt || item.regDt || '',
    progressUpdatedBy: item.ownerNm || '',
    thumbnailBackgroundColor: undefined,
    itemCount: Number(item.itemCount || 0),
    description: item.description || '',
    fileUid: item.fileUid || null,
    thumbnailFileUid: item.thumbnailFileUid || null,
    ownerUid: item.ownerUid,
    regUid: item.regUid,
    startAt: item.startAt || '',
    endAt: item.endAt || '',
  };
}

function mapProjectInfo(item) {
  const project = mapProjectCard(item);
  return {
    ...project,
    content: item.content || '',
    fileUid: item.fileUid || null,
    files: Array.isArray(item.files) ? item.files.map(mapFile) : [],
    thumbnailFiles: Array.isArray(item.thumbnailFiles) ? item.thumbnailFiles.map(mapFile) : [],
    tags: tagsFromFormData(item.formData),
    updtDt: item.updtDt || item.regDt || '',
    updtNm: item.updtNm || '',
  };
}

function mapMilestoneFeedRow(item) {
  const base = mapItemListItem(item);
  const pt = item.projectTitle != null ? String(item.projectTitle).trim() : '';
  const path =
    item.boardPathDisplay != null && String(item.boardPathDisplay).trim() !== ''
      ? String(item.boardPathDisplay).trim()
      : pt
        ? `마일스톤 > ${pt}`
        : '마일스톤';
  return {
    ...base,
    milestoneUid: item.milestoneUid,
    parentMilestoneUid: item.parentMilestoneUid,
    boardPathDisplay: path,
    projectTitle: pt,
    regDt: item.regDt || '',
  };
}

function mapItemListItem(item) {
  return {
    id: item.milestoneUid,
    title: item.title || '',
    summary: listItemSummaryPreview(item),
    tags: tagsFromFormData(item.formData),
    replies: Number(item.commentCnt || 0),
    views: Number(item.viewCnt || 0),
    writtenAt: item.regDt || '',
    authorInitial: (item.ownerNm || '').charAt(0),
    authorName: item.ownerNm || '',
    org: '',
    regUid: item.ownerUid ?? item.regUid,
    userId: item.ownerId || '',
    regId: item.ownerId || '',
    fileUid: item.fileUid || null,
    statusCd: mapStatus(item.statusCd),
    progressRate: Number(item.progressRate || 0),
  };
}

function mapItemDetail(item) {
  return {
    id: item.milestoneUid,
    title: item.title || '',
    body: item.content || '',
    contentHtml: item.content || '',
    tags: tagsFromFormData(item.formData),
    attachments: Array.isArray(item.files) ? item.files.map(mapFile) : [],
    fileUid: item.fileUid || null,
    authorName: item.ownerNm || '',
    org: '',
    ownerUid: item.ownerUid,
    userId: item.ownerId || '',
    regUid: item.regUid,
    writtenAt: item.regDt || '',
    views: Number(item.viewCnt || 0),
    replies: Number(item.commentCnt || 0),
    startAt: item.startAt || '',
    endAt: item.endAt || '',
    statusCd: mapStatus(item.statusCd),
    progressRate: Number(item.progressRate || 0),
    projectId: item.parentMilestoneUid,
  };
}

function mapMilestoneComment(item) {
  return {
    id: String(item.commentUid),
    writer: item.regNm || '',
    author: {
      regUid: item.regUid,
      name: item.regNm || '',
      org: '',
      userId: item.regId || '',
    },
    regUid: item.regUid,
    userId: item.regId || '',
    parentCommentUid: item.parentCommentUid ?? null,
    parentId: item.parentCommentUid ?? null,
    date: item.regDt || '',
    content: item.content || '',
    raw: item,
  };
}

function resolveParentCommentUid(reply) {
  const raw = reply?.parentCommentUid ?? reply?.parentId ?? reply?.raw?.parentCommentUid ?? null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function milestoneCommentsPath(spaceUid, projectUid, itemUid) {
  return `/api/alli/spaces/${spaceUid}/milestones/${projectUid}/items/${itemUid}/comments`;
}

/** 마일스톤 아이템 댓글 — 서버 페이징 (list, totalElements, totalPages, number, size). 정렬: 최신순 */
export async function fetchAllianceMilestoneItemCommentsPage(spaceUid, projectUid, itemUid, params = {}) {
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  const size = params.size ?? ALLIANCE_COMMENT_PAGE_SIZE;
  const sortDir = params.sortDir;
  const res = await api.get(milestoneCommentsPath(spaceUid, projectUid, itemUid), {
    params: { pageNumber, size, ...(sortDir ? { sortDir } : {}) },
  });
  const list = Array.isArray(res?.list) ? res.list : [];
  return {
    items: list.map(mapMilestoneComment),
    totalElements: Number(res?.totalElements ?? 0),
    totalPages: Math.max(0, Number(res?.totalPages ?? 0)),
    number: Number(res?.number ?? pageNumber),
    size: Number(res?.size ?? size),
  };
}

export async function createAllianceMilestoneItemComment(spaceUid, projectUid, itemUid, payload) {
  return api.post(milestoneCommentsPath(spaceUid, projectUid, itemUid), payload);
}

export async function updateAllianceMilestoneItemComment(spaceUid, projectUid, itemUid, commentUid, payload) {
  return api.put(`${milestoneCommentsPath(spaceUid, projectUid, itemUid)}/${commentUid}`, payload);
}

export async function deleteAllianceMilestoneItemComment(spaceUid, projectUid, itemUid, commentUid) {
  return api.delete(`${milestoneCommentsPath(spaceUid, projectUid, itemUid)}/${commentUid}`);
}

/** TaskInfo 게시판 댓글과 동일: 삭제·추가·수정 후 서버 목록 재조회 */
export async function syncAllianceMilestoneItemReplies(
  spaceUid,
  projectUid,
  itemUid,
  previousReplies,
  nextReplies,
  options = {},
) {
  const prevMap = new Map(previousReplies.map((r) => [String(r.id), r]));
  const nextMap = new Map(nextReplies.map((r) => [String(r.id), r]));

  for (const [id] of prevMap.entries()) {
    if (!nextMap.has(id) && !String(id).startsWith('reply-')) {
      await deleteAllianceMilestoneItemComment(spaceUid, projectUid, itemUid, id);
    }
  }

  for (const [id, next] of nextMap.entries()) {
    const prev = prevMap.get(id);
    if (!prev) {
      await createAllianceMilestoneItemComment(spaceUid, projectUid, itemUid, {
        content: next.content || '',
        parentCommentUid: resolveParentCommentUid(next),
      });
      continue;
    }
    if ((prev.content || '') !== (next.content || '')) {
      await updateAllianceMilestoneItemComment(spaceUid, projectUid, itemUid, id, {
        content: next.content || '',
        parentCommentUid: resolveParentCommentUid(next) ?? resolveParentCommentUid(prev),
        fileUid: prev.raw?.fileUid ?? null,
      });
    }
  }

  const sortDir = options.sortDir;
  return fetchAllianceMilestoneItemCommentsPage(spaceUid, projectUid, itemUid, {
    pageNumber: 1,
    size: ALLIANCE_COMMENT_PAGE_SIZE,
    ...(sortDir ? { sortDir } : {}),
  });
}

const MILESTONE_BULK_PAGE_SIZE = 200;

/** 역할·권한 팝업 등 — 페이지를 순회해 스페이스 내 마일스톤 프로젝트 전체 로드 */
export async function fetchAllianceMilestoneProjectsAll(spaceUid) {
  if (!spaceUid) return [];
  const all = [];
  let pageNumber = 1;
  let totalPages = 1;
  while (pageNumber <= totalPages) {
    const res = await fetchAllianceMilestoneProjectsPage(spaceUid, {
      pageNumber,
      size: MILESTONE_BULK_PAGE_SIZE,
    });
    const batch = res.items || [];
    all.push(...batch);
    totalPages = Math.max(1, Number(res.totalPages) || 1);
    pageNumber += 1;
    if (batch.length === 0 && pageNumber > 2) break;
  }
  return all;
}

/** 마일스톤 프로젝트(폴더) 목록 — 서버 페이징 (searchTp: title | org | status) */
export async function fetchAllianceMilestoneProjectsPage(spaceUid, params = {}) {
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  const size = params.size ?? 9;
  const query = { pageNumber, size };
  const rawSearch = params.search != null ? String(params.search).trim() : '';
  if (rawSearch) {
    query.search = rawSearch;
    query.searchTp = params.searchTp || params.searchType || 'title';
  }
  const res = await api.get(`/api/alli/spaces/${spaceUid}/milestones`, { params: query });
  const list = Array.isArray(res?.list) ? res.list : [];
  const totalElements = Number(res?.totalElements ?? res?.totalCount ?? list.length);
  const totalPages = Math.max(0, Number(res?.totalPages ?? 0));
  const number = Number(res?.number ?? pageNumber);
  return {
    items: list.map(mapProjectCard),
    totalElements,
    totalPages,
    number,
    size: Number(res?.size ?? size),
  };
}

export async function fetchAllianceMilestoneProject(spaceUid, projectUid) {
  const res = await api.get(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}`);
  return mapProjectInfo(res);
}

export async function saveAllianceMilestoneProject(spaceUid, payload, projectUid = null) {
  if (projectUid) {
    await api.put(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}`, payload);
    return projectUid;
  }
  return api.post(`/api/alli/spaces/${spaceUid}/milestones`, payload);
}

export async function deleteAllianceMilestoneProject(spaceUid, projectUid) {
  return api.delete(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}`);
}

/** 마일스톤 프로젝트 내 게시글 목록 — 서버 페이징 (searchTp: title | writer | author | content) */
export async function fetchAllianceMilestoneItemsPage(spaceUid, projectUid, params = {}) {
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  const size = params.size ?? 10;
  const query = { pageNumber, size };
  const rawSearch = params.search != null ? String(params.search).trim() : '';
  if (rawSearch) {
    query.search = rawSearch;
    const st = params.searchTp || params.searchType || 'title';
    query.searchTp = st === 'author' ? 'writer' : st;
  }
  const res = await api.get(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}/items`, { params: query });
  const list = Array.isArray(res?.list) ? res.list : [];
  const totalElements = Number(res?.totalElements ?? res?.totalCount ?? list.length);
  const totalPages = Math.max(0, Number(res?.totalPages ?? 0));
  const number = Number(res?.number ?? pageNumber);
  return {
    items: list.map(mapItemListItem),
    totalElements,
    totalPages,
    number,
    size: Number(res?.size ?? size),
  };
}

/** 스페이스 내 마일스톤 게시물(ITEM) 통합 피드 — 최신 등록순, 서버 페이징 */
export async function fetchAllianceMilestonePostFeed(spaceUid, params = {}) {
  const pageNumber = params.pageNumber ?? params.page ?? 1;
  const size = params.size ?? 10;
  const query = { pageNumber, size };
  const rawSearch = params.search != null ? String(params.search).trim() : '';
  if (rawSearch) {
    query.search = rawSearch;
    const st = params.searchTp || params.searchType || 'title';
    query.searchTp = st === 'author' ? 'writer' : st;
  }
  const res = await api.get(`/api/alli/spaces/${spaceUid}/milestone-post-feed`, { params: query });
  const list = Array.isArray(res?.list) ? res.list : [];
  const totalElements = Number(res?.totalElements ?? res?.totalCount ?? list.length);
  const totalPages = Math.max(0, Number(res?.totalPages ?? 0));
  const number = Number(res?.number ?? pageNumber);
  return {
    list,
    items: list.map(mapMilestoneFeedRow),
    totalElements,
    totalPages,
    number,
    size: Number(res?.size ?? size),
    totalCount: totalElements,
  };
}

const inflightMilestoneItemDetail = new Map();

export async function fetchAllianceMilestoneItemDetail(spaceUid, projectUid, itemUid, options = {}) {
  const sortDir = options.sortDir;
  const sortKey = sortDir != null ? String(sortDir) : '';
  const key = `${spaceUid}:${projectUid}:${itemUid}:${sortKey}`;
  const existing = inflightMilestoneItemDetail.get(key);
  if (existing) return existing;
  const promise = (async () => {
    const [res, commentsPage] = await Promise.all([
      api.get(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}/items/${itemUid}`),
      fetchAllianceMilestoneItemCommentsPage(spaceUid, projectUid, itemUid, {
        pageNumber: 1,
        size: ALLIANCE_COMMENT_PAGE_SIZE,
        ...(sortDir ? { sortDir } : {}),
      }).catch(() => ({
        items: [],
        totalElements: 0,
        totalPages: 0,
        number: 1,
        size: ALLIANCE_COMMENT_PAGE_SIZE,
      })),
    ]);
    const item = mapItemDetail(res);
    const totalFromPage = commentsPage.totalElements;
    const totalElements =
      totalFromPage != null && Number.isFinite(Number(totalFromPage))
        ? Number(totalFromPage)
        : Number(item.replies ?? res?.commentCnt ?? 0);
    return {
      item: res,
      post: mapItemListItem(res),
      detail: item,
      comments: commentsPage.items,
      commentTotalElements: totalElements,
    };
  })();
  inflightMilestoneItemDetail.set(key, promise);
  promise.finally(() => inflightMilestoneItemDetail.delete(key));
  return promise;
}

export async function saveAllianceMilestoneItem(spaceUid, projectUid, payload, itemUid = null) {
  if (itemUid) {
    await api.put(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}/items/${itemUid}`, payload);
    return itemUid;
  }
  return api.post(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}/items`, payload);
}

export async function deleteAllianceMilestoneItem(spaceUid, projectUid, itemUid) {
  return api.delete(`/api/alli/spaces/${spaceUid}/milestones/${projectUid}/items/${itemUid}`);
}
